import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Col, Spinner, Table} from "reactstrap";
import {useHistory, useLocation} from "react-router-dom";
import queryParser from "../../helpers/queryStringParser";
import qs from "qs";
import {CLINICIANS_LIMIT} from "../../constants/paginationLimits";
import PaginationTableFooter from "../patientManagement/tableComponents/paginationFooter";
import parseIntFromStr from "../../helpers/parseIntFromStr";
import NoCliniciansPlaceholder from "./cliniciansComponents/noCliniciansPlaceholder";
import {cliniciansColumns} from "./tableComponents/cliniciansDataStructure";
import RoleHistoryModal from "./cliniciansComponents/RoleHistoryModal";
import OwnPatientsOnlyNote from "./cliniciansComponents/OwnPatientsOnlyNote";
import CancelModal from "../../components/manageSessionModal/cancelModal";
import AppSettingsService from "../../services/appSettings";
import StorageService from "../../services/StorageService";
import {USER} from "../../constants/storageConstants";
import {ADMIN, ROLE_LABELS, SUPER_ADMIN} from "../../constants/roles";
import * as _ from "lodash";
import TableHeader from "../patientManagement/tableComponents/tableHeader";
import SimpleTableBody from "../../components/simpleTable/simpleTableBody";
import CreateClinicianForm from "./cliniciansComponents/createClinicianForm";
import CliniciansService from "../../services/clinicians";
import S3Service from "../../services/S3Service";
import {FILE_TYPES} from "../../constants/uploadFileConstants";
import toastr from "toastr";
import clearStoredSession from "../../helpers/clearStoredSession";

const CREATE_SUCCESS_MESSAGE = "The invitation has been successfully sent";
const UPDATE_SUCCESS_MESSAGE = "The information has been successfully updated";
export const SIGN_OUT_NOT_CONFIRMED_MESSAGE =
    "Saved, but signing them out could not be confirmed. A super admin can use Sign out everywhere.";
export const OWN_EMAIL_CHANGED_MESSAGE = "Your login email has been changed. Please sign in again with the new address.";
export const SIGNATURE_UPLOAD_FAILED_MESSAGE = "The signature image could not be uploaded. Please try again.";

/**
 * A login email as the server compares it: case and surrounding spaces aside (it stores and matches
 * addresses lowercased), so a case-only edit is no change there and is not asked about here.
 */
export const sameEmail = (a, b) => String(a ?? "").trim().toLowerCase() === String(b ?? "").trim().toLowerCase();

/**
 * The picked signature image, uploaded. Answers its file id, or null - after an error toast - when the
 * upload failed (or came back without an id): the caller sends nothing and leaves the form open.
 */
const uploadSignature = async (file) => {
    try {
        const fileData = await S3Service.uploadFile(file, FILE_TYPES.SIGNATURE);
        if (fileData?.id) {
            return fileData.id;
        }
    } catch {
        // Shown below: the direct post to S3 does not go through the API layer, which shows the other errors.
    }
    toastr.error(SIGNATURE_UPLOAD_FAILED_MESSAGE);
    return null;
};

/**
 * What the confirmation says before a login email is changed (by a super admin or an Admin). The server
 * signs the person out everywhere and cancels every reset / invitation link and one-time password
 * before it writes the new address, then emails both addresses.
 */
export const emailChangeText = ({ from, to, isSelf }) => (
    `${from} → ${to}. They will be signed out of every device, including a call in progress. `
    + "Any password-reset or invitation link already sent stops working. A notice goes to both addresses."
    + (isSelf ? " You will be signed out too." : "")
);

const Clinicians = () => {
    const history = useHistory();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [isOpenCreateClinicianModal, setIsOpenCreateClinicianModal] = useState(false);
    const [editClinicianData, setEditClinicianData] = useState(null);
    const [cliniciansPagination, setCliniciansPagination] = useState({});
    const [clinicians, setClinicians] = useState([]);

    const searchQuery = queryParser(location.search)

    const setNewSearchQuery = (incomeQuery) => {
        const newQuery = {
            ...searchQuery,
            ...incomeQuery,
        }
        const query = qs.stringify(newQuery);
        history.push({
            search: query,
        });
    }

    const handleChangePage = ({selected}) => {
        if (!isNaN(selected)) {
            setNewSearchQuery({offset: selected * CLINICIANS_LIMIT});
        }
    }

    const handleEdit = useCallback((admin) => {
        const {
            id,
            firstName,
            lastName,
            email,
            doctor: {
                doctorSignId,
                gmc,
                link,
            },
        } = admin;

        setEditClinicianData({
            id,
            firstName,
            lastName,
            email,
            doctorSignId,
            gmc,
            link,
        })
    }, [setEditClinicianData])

    // Super admins and Admins create clinicians and edit them (an Admin only Doctors, never themselves -
    // the server refuses anything else), and grant a Doctor every doctor's patients in the default lists.
    // Only a super admin changes roles, signs someone out everywhere or sees role history.
    const signedIn = new StorageService().getValueByKey(USER);
    const isUserSuperAdmin = signedIn?.role === SUPER_ADMIN;
    const mayManageClinicians = isUserSuperAdmin || signedIn?.role === ADMIN;
    const currentUserId = signedIn?.id;

    // A save that changes the login email, waiting for confirmation: { values, from, isSelf }.
    const [pendingEmailChange, setPendingEmailChange] = useState(null);

    // A role change, or a "Sign out everywhere" (signOut: true), waiting for confirmation.
    const [pendingRole, setPendingRole] = useState(null);
    const [historyFor, setHistoryFor] = useState(null);

    const toggleFullAccess = useCallback(async (clinician, enabled) => {
        try {
            await AppSettingsService.setFullPatientAccess(clinician.id, enabled);
            setClinicians(previous => previous.map(item => (
                item.id === clinician.id ? { ...item, hasFullPatientAccess: enabled } : item
            )));
        } catch {
            // The interceptor has already shown the error; the checkbox stays as it was.
        }
    }, []);

    // Choosing a role only asks. Whether they look to be mid-consultation is fetched first, so the
    // confirmation can warn that the change will sign them out of the call.
    const requestRoleChange = useCallback(async (clinician, toRole) => {
        let inConsultation = false;
        try {
            const { data } = await AppSettingsService.getClinicianConsultationStatus(clinician.id);
            inConsultation = Boolean(data?.inConsultation);
        } catch {
            // Only a warning: carry on without it.
        }
        setPendingRole({ clinician, toRole, inConsultation });
    }, []);

    const requestSignOut = useCallback(async (clinician) => {
        let inConsultation = false;
        try {
            const { data } = await AppSettingsService.getClinicianConsultationStatus(clinician.id);
            inConsultation = Boolean(data?.inConsultation);
        } catch {
            // Only a warning: carry on without it.
        }
        setPendingRole({ clinician, toRole: clinician.role, inConsultation, signOut: true });
    }, []);

    const showHistory = useCallback((clinician) => setHistoryFor(clinician), []);

    const superAdminTools = useMemo(() => (isUserSuperAdmin ? {
        onRequestRoleChange: requestRoleChange,
        onRequestSignOut: requestSignOut,
        onShowHistory: showHistory,
    } : null), [isUserSuperAdmin, requestRoleChange, requestSignOut, showHistory]);

    const { header: tableHeader, structure: dataStructure } = useMemo(
        () => cliniciansColumns({
            handleEdit: mayManageClinicians ? handleEdit : null,
            currentUserId,
            superAdmin: superAdminTools,
            onToggleFullAccess: mayManageClinicians ? toggleFullAccess : null,
        }),
        [handleEdit, mayManageClinicians, currentUserId, superAdminTools, toggleFullAccess]
    );

    const getClinicians = useCallback(async () => {
        try {
            setIsLoading(true);
            const {data, pagination} = await CliniciansService.getCliniciansList({
                limit: CLINICIANS_LIMIT,
                offset: searchQuery.offset
            });

            setClinicians(data);
            setCliniciansPagination(pagination);
        } finally {
            setIsLoading(false);
        }
    }, [setIsLoading, setClinicians, setCliniciansPagination, searchQuery.offset])

    const handleCreateClinician = useCallback(async (values) => {
        const {file, ...rest} = values || {};
        const {firstName, lastName, email, gmc} = rest || {};

        // A failed upload: the error is shown and the form stays open with what was typed.
        const doctorSignId = await uploadSignature(file);
        if (!doctorSignId) {
            return;
        }

        try {
            await CliniciansService.createClinician({
                firstName,
                lastName,
                email,
                gmc,
                doctorSignId,
            })
            toastr.success(CREATE_SUCCESS_MESSAGE);
            await getClinicians();
        } finally {
            setIsOpenCreateClinicianModal(false);
        }
    }, [setIsOpenCreateClinicianModal])

    // ownEmailChange: a super admin changing their own login email - signed out here too afterwards.
    const saveClinician = async (values, { ownEmailChange = false } = {}) => {
        const {id, file, firstName, lastName, email, gmc, doctorSignId} = values || {};
        try {
            // Only a signature image picked in the form is uploaded, and only then is a new doctorSignId
            // sent: the server treats a changed doctorSignId as a replaced signature (it must be a file
            // this admin uploaded, and the doctor is emailed). Otherwise the current one goes back as it was.
            // A failed upload: the error is shown, nothing is saved, the form stays open.
            let signId = doctorSignId;
            if (file) {
                signId = await uploadSignature(file);
                if (!signId) {
                    return;
                }
            }

            const response = await CliniciansService.updateClinician(id, {
                firstName,
                lastName,
                email,
                gmc,
                doctorSignId: signId,
            });
            const result = (response && response.data) || {};
            setEditClinicianData(null);

            if (result.emailChanged && result.sessionsEnded === false) {
                toastr.warning(SIGN_OUT_NOT_CONFIRMED_MESSAGE);
            } else {
                toastr.success(ownEmailChange && result.emailChanged !== false ? OWN_EMAIL_CHANGED_MESSAGE : UPDATE_SUCCESS_MESSAGE);
            }

            // Their own login email changed: the server has signed them out everywhere, so forget the
            // session here too and go to the login page (unless the server says the email stayed).
            if (ownEmailChange && result.emailChanged !== false) {
                clearStoredSession();
                history.push("/login");
                return;
            }
            await getClinicians();
        } catch {
            // The server's message (e.g. that email is taken) has already been shown. The form stays
            // open with what was typed.
        }
    };

    // Saving asks first when the login email differs from the one loaded - as the server compares them,
    // case and surrounding spaces aside (a case-only edit changes nothing there, so nothing to confirm).
    const handleUpdateClinician = (values) => {
        const loaded = editClinicianData || {};
        if (!sameEmail(values?.email, loaded.email)) {
            setPendingEmailChange({ values, from: loaded.email, isSelf: loaded.id === currentUserId });
            return;
        }
        saveClinician(values);
    };

    const confirmEmailChange = () => {
        const { values, isSelf } = pendingEmailChange;
        setPendingEmailChange(null);
        saveClinician(values, { ownEmailChange: isSelf });
    };

    useEffect(() => {
        getClinicians();
    }, [getClinicians])

    const confirmRoleChange = async () => {
        const { clinician, toRole, signOut } = pendingRole;
        setPendingRole(null);

        if (signOut) {
            try {
                await AppSettingsService.signOutClinician(clinician.id);
                toastr.success(`${clinician.firstName} ${clinician.lastName} has been signed out everywhere.`);
            } catch {
                // The interceptor has shown the error.
            }
            return;
        }

        try {
            // The role this page shows goes too: if someone changed it since, the server refuses.
            const { data } = await AppSettingsService.changeClinicianRole(clinician.id, toRole, clinician.role);
            // Merge what the server says, never just the role: a change also withdraws a grant.
            setClinicians(previous => previous.map(item => (
                item.id === clinician.id
                    ? { ...item, role: data.role, hasFullPatientAccess: data.hasFullPatientAccess }
                    : item
            )));
            toastr.success(data.changed
                ? `${clinician.firstName} ${clinician.lastName} is now ${ROLE_LABELS[data.role]} and has been signed out everywhere.`
                : `${clinician.firstName} ${clinician.lastName} has been signed out everywhere.`);
        } catch {
            // The interceptor has shown the error. The role may still have changed (it is changed
            // before the sessions are ended), so reload the list rather than guess.
            await getClinicians();
        }
    };

    const pendingText = () => {
        if (!pendingRole) {
            return '';
        }
        const { clinician, toRole, inConsultation } = pendingRole;
        const name = `${clinician.firstName} ${clinician.lastName}`;
        const consultation = inConsultation
            ? ` ${name} looks to be in a consultation right now: they will be signed out on their next action, `
                + 'anything unsaved will be lost, and ending the call will not close it for the patient. '
                + 'You may want to wait until the call has finished.'
            : '';
        return pendingRole.signOut
            ? `${name} will be signed out of every session, on every device, straight away. `
                + `Their role stays ${ROLE_LABELS[toRole]}.${consultation}`
            : `${name} will change from ${ROLE_LABELS[clinician.role]} to ${ROLE_LABELS[toRole]}, and will be signed `
                + `out of every session, on every device, straight away. They will need to log in again.${consultation}`;
    };

    return (
        <div className="page-content mx-2 pb-2">
            <div className="card justify-content-between pr-20px pl-20px pt-20px activate-code-page">
                <div className="d-flex flex-wrap justify-content-between align-items-end h-100 mb-4">
                    <Col sm={6} lg={4} className="p-0">
                        <label className="details-caption">
                            Clinicians management
                        </label>
                    </Col>
                    {mayManageClinicians && (
                        <Col sm={5} lg={4} className="p-0 header-button">
                            <button
                                className="btn btn-primary call-form-button w-100  mt-1 mt-sm-0"
                                onClick={() => setIsOpenCreateClinicianModal(true)}
                            >
                                Create new clinician
                            </button>
                        </Col>
                    )}
                </div>
                {isLoading &&
                    <div className="patients-table d-flex justify-content-center align-items-center">
                        <Spinner className="mx-auto" color="info"/>
                    </div>}
                {!isLoading && clinicians.length > 0 && (
                    <>
                        {isUserSuperAdmin && <OwnPatientsOnlyNote/>}
                        <section className="activate-code-table-wrap">
                            <Table className="table mb-0">
                                <TableHeader columns={tableHeader} clickCellAction={_.noop}/>
                                <SimpleTableBody data={clinicians} structure={dataStructure}/>
                            </Table>
                        </section>
                        {cliniciansPagination?.totalCount && (
                            <section className="mt-4">
                                <PaginationTableFooter
                                    total={cliniciansPagination.totalCount}
                                    handleChangePage={handleChangePage}
                                    currentOffset={parseIntFromStr(searchQuery.offset)}
                                    limit={CLINICIANS_LIMIT}
                                />
                            </section>
                        )}
                    </>
                )}
                {!isLoading && !clinicians.length && <NoCliniciansPlaceholder/>}
            </div>
            <CreateClinicianForm
                isOpen={isOpenCreateClinicianModal}
                toggle={() => setIsOpenCreateClinicianModal(false)}
                handleSubmit={handleCreateClinician}
            />
            {pendingRole && (
                <CancelModal
                    visibleModal={!!pendingRole}
                    setVisibleModal={() => setPendingRole(null)}
                    handleClose={confirmRoleChange}
                    header={pendingRole.signOut
                        ? "Sign out everywhere?"
                        : `Change role to ${ROLE_LABELS[pendingRole.toRole]}?`}
                    text={pendingText()}
                    cancelButtonTitle="Cancel"
                />
            )}
            {historyFor && <RoleHistoryModal clinician={historyFor} onClose={() => setHistoryFor(null)}/>}
            <CreateClinicianForm
                isEdit
                isOpen={Boolean(editClinicianData)}
                initialValues={editClinicianData}
                toggle={() => setEditClinicianData(null)}
                handleSubmit={handleUpdateClinician}
            />
            {pendingEmailChange && (
                <CancelModal
                    visibleModal={!!pendingEmailChange}
                    setVisibleModal={() => setPendingEmailChange(null)}
                    handleClose={confirmEmailChange}
                    header="Change login email?"
                    text={emailChangeText({
                        from: pendingEmailChange.from,
                        to: pendingEmailChange.values.email,
                        isSelf: pendingEmailChange.isSelf,
                    })}
                    cancelButtonTitle="Cancel"
                />
            )}
        </div>
    )
}

export default Clinicians;